import { useState, useEffect, useRef, useCallback } from 'react';
import config from '../config';

export const useWebSocket = (userId, selectedSession, autoReconnect, setRefreshAll) => {
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentResponse, setCurrentResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [followUpSuggestions, setFollowUpSuggestions] = useState([]);
  const [sessionAnalytics, setSessionAnalytics] = useState(null);
  const [userStats, setUserStats] = useState(null);
  
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    console.log('connect:', currentSessionId, selectedSession);
    const wsUrl = `${config.webSocketUrl}/${userId}-${selectedSession?.id}`;
    const newSocket = new WebSocket(wsUrl);

    newSocket.onopen = () => {
      console.log('onopen:', currentSessionId, selectedSession);
      console.log('WebSocket connected');
      // console.log(newSocket);
      setConnectionStatus('Connected');
      setSocket(newSocket);
      socketRef.current = newSocket;
      reconnectAttempts.current = 0;
    };

    newSocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWebSocketMessage(data);
    };

    newSocket.onclose = (event) => {
      console.log('onclose:', currentSessionId, selectedSession);
      console.log('WebSocket disconnected:', event.code, event.reason);
      setConnectionStatus('Disconnected');
      setSocket(null);
      socketRef.current = null;

      if (autoReconnect) {
        console.log('autoReconnect:', currentSessionId, selectedSession);
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.pow(2, reconnectAttempts.current) * 3000;
          console.log(`Reconnecting attempt ${reconnectAttempts.current} of ${maxReconnectAttempts} in ${delay}ms...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
            setRefreshAll(true);
          }, delay);
        }
      }
    };

    newSocket.onerror = (error) => {
      console.log('onerror:', currentSessionId, selectedSession);
      console.error('WebSocket error:', error);
      setConnectionStatus('Error');
    };

  }, [userId, selectedSession]);

  useEffect(() => {
    console.log('useEffect:', currentSessionId, selectedSession);
    setMessages([]);

    if ( selectedSession.length === 0 ) { 
      setCurrentSessionId(null);
      return;
    }

    setCurrentSessionId(selectedSession?.id);

    selectedSession.messages?.map((data) => {
      setMessages(prev => [...prev, {
        type: 'user',
        content: data.message,
        timestamp: data.timestamp,
      }]);

      setMessages(prev => [...prev, {
        type: 'ai',
        content: data.response,
        timestamp: data.timestamp,
      }]);
    });
  }, [selectedSession]);

  const handleWebSocketMessage = (data) => {
    console.log('handleWebSocketMessage:', currentSessionId, selectedSession);
    console.log('WebSocket message:', data.type, data);
    switch (data.type) {
      case 'connection_established':
        setUserStats(data.user_stats);
        break;
        
      case 'session_created':
        setCurrentSessionId(data.session_id);
        break;
        
      case 'user_message':
        // setIsStreaming(true);
        setMessages(prev => [...prev, {
          type: 'user',
          content: data.content,
          timestamp: new Date().toISOString(),
          intent: data.intent
        }]);
        break;
        
      case 'ai_typing_start':
        // Show typing indicator
        break;
        
      case 'ai_response_start':
        setCurrentResponse('');
        setFollowUpSuggestions([]);
        setIsStreaming(true);
        break;
        
      case 'ai_response_chunk':
        setCurrentResponse(prev => prev + data.content);
        break;
        
      case 'ai_response_complete':
        setMessages(prev => [...prev, {
          type: 'ai',
          content: data.full_response,
          timestamp: new Date().toISOString()
        }]);
        setIsStreaming(false);
        // setCurrentResponse('');
        break;
        
      case 'follow_up_suggestions':
        setFollowUpSuggestions(data.suggestions);
        break;
        
      case 'session_analytics':
        setSessionAnalytics(data.analytics);
        break;
        
      case 'session_title_updated':
        break;
        
      case 'generation_stopped':
        setIsStreaming(false);
        break;
        
      case 'error':
        // console.error('Chat error:', data.message);
        setMessages(prev => [...prev, {
          type: 'error',
          content: data.message,
          timestamp: new Date().toISOString()
        }]);
        break;
        
      default:
        // console.log('Unknown message type:', data.type);
    }
  };

  const sendMessage = useCallback((message, sessionId = null, chatTitle = 'New Chat') => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'chat',
        message,
        session_id: sessionId,
        chat_title: chatTitle,
      }));
      return true;
    }
    return false;
  }, []);

  const stopGeneration = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'stop_generation'
      }));
    }
  }, []);

  const getSuggestions = useCallback((sessionId) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'get_suggestions',
        session_id: sessionId
      }));
    }
  }, []);

  const performSessionAction = useCallback((action, sessionId, data = {}) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'session_action',
        action,
        session_id: sessionId,
        ...data
      }));
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (socketRef.current) {
      socketRef.current.close();
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    socket,
    connectionStatus,
    currentSessionId,
    messages,
    currentResponse,
    isStreaming,
    followUpSuggestions,
    sessionAnalytics,
    userStats,
    sendMessage,
    stopGeneration,
    getSuggestions,
    performSessionAction,
    connect,
    disconnect,
  };
};

